import React, { useState, useEffect, useCallback } from 'react';
import API from '../../../utils/api';
import { useApp } from '../../../context/AppContext';
import { Modal, Btn, FormGroup, Input, CustomSelect, InfoBanner, Card, FormGrid, Table, Tr, Td } from '../../common/UI';

const ROLE_LABELS = { admin: 'Administrator', warehouse: 'Warehouse Staff', customer: 'Customer' };
const ROLE_COLORS = {
    admin: { bg: '#eff6ff', color: '#1d4ed8' },
    warehouse: { bg: '#fef9c3', color: '#854d0e' },
    customer: { bg: '#f0fdf4', color: '#15803d' },
};

export default function AdminUsers() {
    const { customers, showToast, users, fetchAllData } = useApp();
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 9;
    const [showForm, setShowForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null); // { id, name }

    const [form, setForm] = useState({ name: '', email: '', role: 'customer', customer_id: '', password: '', confirmPassword: '' });

    const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

    const handleSubmit = async e => {
        e.preventDefault();
        if (form.password !== form.confirmPassword) {
            showToast('Passwords do not match!', 'error');
            return;
        }
        setSubmitting(true);
        try {
            await API.post('/admin/users', {
                name: form.name, email: form.email,
                role: form.role, customer_id: form.role === 'customer' ? form.customer_id : null,
                password: form.password
            });
            showToast(`✓ User "${form.name}" created successfully!`, 'success');
            setForm({ name: '', email: '', role: 'customer', customer_id: '', password: '', confirmPassword: '' });
            setShowForm(false);
            await fetchAllData();
        } catch (err) {
            showToast(err.response?.data?.message || 'Error creating user.', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        const { id, name, customer_id } = confirmDelete;
        
        // If linked to a customer, delete the customer profile too
        try {
            if (customer_id && !id.preventCustomerDelete) {
                 await API.delete(`/admin/customers/${customer_id}`);
            }
            await API.delete(`/admin/users/${id}`);
            await fetchAllData();
            showToast(`✓ User "${name}" and profile deleted.`, 'success');
            setConfirmDelete(null);
        } catch (err) {
            showToast('Could not delete user. Check dependencies.', 'error');
        }
    };

    const inp = {
        width: '100%', padding: '9px 12px', borderRadius: 8,
        border: '1.5px solid var(--border)', background: 'var(--surface)',
        color: 'var(--text)', fontFamily: 'inherit', fontSize: 14,
        outline: 'none', transition: 'border-color .2s', boxSizing: 'border-box'
    };

    const totalPages = Math.ceil((users || []).length / itemsPerPage);
    const currentItems = (users || []).slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <div className="page-root" style={{ maxWidth: 960, margin: '0 auto' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: 0 }}>👤 User Management</h1>
                    <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
                        Create and manage system users. Customers, warehouse staff, and admins.
                    </p>
                </div>
                <button onClick={() => setShowForm(p => !p)}
                    style={{
                        padding: '7px 16px', borderRadius: 6, border: 'none',
                        background: showForm ? 'var(--surface2)' : 'var(--accent)',
                        color: showForm ? 'var(--text2)' : '#fff',
                        fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'all .2s'
                    }}>
                    {showForm ? '✕ Cancel' : '+ Add User'}
                </button>
            </div>

            {/* Add User Form */}
            {showForm && (
                <Card style={{ padding: 24, marginBottom: 24 }}>
                    <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 20, marginTop: 0 }}>
                        Create New User
                    </h2>
                    <form onSubmit={handleSubmit}>
                        <FormGrid style={{ marginBottom: 16 }}>
                            <FormGroup label="Full Name *" required>
                                <Input name="name" value={form.name} onChange={handleChange} placeholder="Enter full name" />
                            </FormGroup>
                            <FormGroup label="Email *" required>
                                <Input name="email" type="email" value={form.email} onChange={handleChange} placeholder="user@example.com" />
                            </FormGroup>
                            <FormGroup label="Role *" required>
                                <CustomSelect name="role" value={form.role} onChange={handleChange}>
                                    <option value="customer">Customer / Agent</option>
                                    <option value="warehouse">Warehouse Staff</option>
                                    <option value="admin">Administrator</option>
                                </CustomSelect>
                            </FormGroup>
                            
                            {form.role === 'customer' && (
                                <FormGroup label="Link to Customer Company *" required>
                                    <CustomSelect name="customer_id" value={form.customer_id} onChange={handleChange}>
                                        <option value="">Select company...</option>
                                        {customers.map(c => <option key={c.id} value={c.id}>{c.company}</option>)}
                                    </CustomSelect>
                                </FormGroup>
                            )}

                            <FormGroup label="Password *" required>
                                <Input name="password" type="password" value={form.password} onChange={handleChange} placeholder="Min 8 chars, 1 uppercase" />
                            </FormGroup>
                            <FormGroup label="Confirm Password *" required>
                                <Input name="confirmPassword" type="password" value={form.confirmPassword} onChange={handleChange} placeholder="Repeat password" />
                            </FormGroup>
                        </FormGrid>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <Btn variant="outline" onClick={() => setShowForm(false)}>Cancel</Btn>
                            <Btn variant="primary" type="submit" disabled={submitting}>
                                {submitting ? 'Creating...' : '✓ Create User'}
                            </Btn>
                        </div>
                    </form>
                </Card>
            )}

            {/* Users Table */}
            <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', boxShadow: 'var(--shadow)' }}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>All Users</span>
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>{users?.length || 0} total</span>
                </div>

                {!users || users.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>No users found. Add the first user above.</div>
                ) : (
                    <div className="admin-users-table-wrap">
                <Table headers={['Name', 'Email', 'Role', 'Status', 'Created', 'Action']}>
                        {currentItems.map(u => {
                            const rc = ROLE_COLORS[u.role] || ROLE_COLORS.customer;
                            return (
                                <Tr key={u.id}>
                                    <Td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <div style={{
                                                width: 32, height: 32, borderRadius: '50%',
                                                background: 'linear-gradient(135deg, var(--accent), var(--accent2))',
                                                color: '#fff', fontSize: 12, fontWeight: 700,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                            }}>
                                                {u.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                                            </div>
                                            <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}>{u.name}</span>
                                        </div>
                                    </Td>
                                    <Td>{u.email}</Td>
                                    <Td>
                                        <span style={{
                                            padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                                            background: rc.bg, color: rc.color
                                        }}>
                                            {ROLE_LABELS[u.role] || u.role}
                                        </span>
                                    </Td>
                                    <Td>
                                        <span style={{
                                            padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                                            background: u.is_verified ? '#f0fdf4' : '#fef9c3',
                                            color: u.is_verified ? '#15803d' : '#854d0e'
                                        }}>
                                            {u.is_verified ? '✓ Verified' : '⏳ Pending'}
                                        </span>
                                    </Td>
                                    <Td muted>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</Td>
                                    <Td>
                                        <button
                                            onClick={() => setConfirmDelete({ id: u.id, name: u.name, customer_id: u.customer_id })}
                                            style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
                                        </button>
                                    </Td>
                                </Tr>
                            );
                        })}
                    </Table>
                </div>
                )}

                {(!(!users || users.length === 0) && totalPages > 1) && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderTop: '1px solid var(--border)', background: 'var(--white)' }}>
                        <Btn variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</Btn>
                        
                        <div style={{ display: 'flex', gap: 6 }}>
                            {Array.from({ length: totalPages }, (_, idx) => idx + 1).map(page => (
                                <button key={page} onClick={() => setCurrentPage(page)}
                                    style={{
                                        width: 28, height: 28, borderRadius: 6, border: 'none', cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                                        background: currentPage === page ? 'var(--accent)' : 'transparent',
                                        color: currentPage === page ? '#fff' : 'var(--text2)',
                                        transition: 'all .15s'
                                    }}>
                                    {page}
                                </button>
                            ))}
                        </div>
                        
                        <Btn variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</Btn>
                    </div>
                )}
            </div>

            {/* Confirm Delete Modal */}
            <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)}
                title="Confirm Deletion" subtitle="This action cannot be undone."
                icon="🗑" iconBg="#fee2e2"
                footer={<>
                    <Btn variant="outline" onClick={() => setConfirmDelete(null)}>No, Cancel</Btn>
                    {confirmDelete?.customer_id && customers.find(c => c.id === confirmDelete.customer_id)?.activeStock > 0 ? (
                        <Btn variant="danger" disabled>Locked</Btn>
                    ) : (
                        <Btn variant="danger" onClick={handleDelete}>Yes, Delete Everything</Btn>
                    )}
                </>}>
                <div style={{ padding: '10px 0' }}>
                    {confirmDelete?.customer_id && customers.find(c => c.id === confirmDelete.customer_id)?.activeStock > 0 && (
                        <div style={{ background: '#fef2f2', border: '1px solid #fee2e2', padding: 12, borderRadius: 8, marginBottom: 12, color: '#dc2626', fontSize: 13, fontWeight: 500 }}>
                            ⚠️ <b>Action Blocked:</b> This user is linked to a customer with <b>{customers.find(c => c.id === confirmDelete.customer_id)?.activeStock} Active Stock</b> entries. Delete is not allowed until stock is cleared.
                        </div>
                    )}
                    Are you sure you want to delete user <b>{confirmDelete?.name}</b>? 
                    {confirmDelete?.customer_id ? " This will also delete their Customer Company Profile permanently." : " They will no longer be able to access the system."}
                </div>
            </Modal>
        </div>
    );
}
